import { useEffect, useMemo, useState } from 'react';
import { FiBriefcase, FiCheck, FiEdit2, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import ImageUploader from './ImageUploader';
import FileUploader from './FileUploader';
import { applicationService } from '../services/applicationService';
import { getUserFacingError } from '../services/http';

type WorkHistory = {
  position: string;
  company_name: string;
  join_date: string;
  end_date: string;
  jobdesc: string;
};

type CandidateForm = {
  name: string;
  email: string;
  phone: string;
  gender: string;
  birth_date: string;
  age: string;
  domicile: string;
  socmed_url: string;
  resources: string;
  position: string;
  height: string;
  weight: string;
  description: string;
  photogrid: string;
  cv: string;
  level: string;
  major: string;
  institute: string;
  location: string;
  year_end: string;
  jobs: WorkHistory[];
};

const emptyHistory = (): WorkHistory => ({
  position: '',
  company_name: '',
  join_date: '',
  end_date: '',
  jobdesc: '',
});

const emptyForm = (): CandidateForm => ({
  name: '',
  email: '',
  phone: '',
  gender: '',
  birth_date: '',
  age: '',
  domicile: '',
  socmed_url: '',
  resources: '',
  position: '',
  height: '',
  weight: '',
  description: '',
  photogrid: '',
  cv: '',
  level: '',
  major: '',
  institute: '',
  location: '',
  year_end: '',
  jobs: [],
});

const normalizeDate = (value: unknown) => {
  const normalized = String(value || '');
  return normalized ? normalized.slice(0, 10) : '';
};

const mapProfileToForm = (profile: any): CandidateForm => {
  const education = profile?.outsource_education || {};
  const histories = Array.isArray(profile?.outsource_job_history)
    ? profile.outsource_job_history
    : [];

  return {
    name: String(profile?.name || ''),
    email: String(profile?.email || ''),
    phone: String(profile?.phone || ''),
    gender: String(profile?.gender || ''),
    birth_date: normalizeDate(profile?.birth_date),
    age: String(profile?.age || ''),
    domicile: String(profile?.domicile || ''),
    socmed_url: String(profile?.socmed_url || ''),
    resources: String(profile?.resources || ''),
    position: String(profile?.position || ''),
    height: String(profile?.height || ''),
    weight: String(profile?.weight || ''),
    description: String(profile?.description || ''),
    photogrid: String(profile?.photogrid || ''),
    cv: String(profile?.cv || ''),
    level: String(education?.level || ''),
    major: String(education?.major || ''),
    institute: String(education?.institute || ''),
    location: String(education?.location || ''),
    year_end: String(education?.year_end || ''),
    jobs: histories.map((history: any) => ({
      position: String(history?.position || ''),
      company_name: String(history?.company_name || ''),
      join_date: normalizeDate(history?.join_date),
      end_date: normalizeDate(history?.end_date),
      jobdesc: String(history?.jobdesc || ''),
    })),
  };
};

const CandidateProfileEditor = () => {
  const [profile, setProfile] = useState<any>(null);
  const [form, setForm] = useState<CandidateForm>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    applicationService.getCandidateProfile()
      .then((response) => {
        const candidate = response.data?.data || null;
        setProfile(candidate);
        setForm(candidate ? mapProfileToForm(candidate) : emptyForm());
      })
      .catch((error) => {
        setNotice({
          type: 'error',
          message: getUserFacingError(error, 'Profil kandidat belum dapat dimuat.').message,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const hasProfile = Boolean(profile?.outsource_id);
  const profileSummary = useMemo(
    () => [
      { label: 'Domisili', value: profile?.domicile },
      { label: 'Jenis kelamin', value: profile?.gender === 'male' ? 'Laki-laki' : profile?.gender === 'female' ? 'Perempuan' : '' },
      { label: 'Tanggal lahir', value: normalizeDate(profile?.birth_date) },
      { label: 'Tinggi / berat', value: profile?.height || profile?.weight ? `${profile?.height || '-'} cm / ${profile?.weight || '-'} kg` : '' },
      { label: 'Pendidikan', value: profile?.outsource_education?.level },
    ],
    [profile]
  );

  const updateField = (field: keyof CandidateForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateHistory = (index: number, field: keyof WorkHistory, value: string) => {
    setForm((current) => ({
      ...current,
      jobs: current.jobs.map((history, historyIndex) => (
        historyIndex === index ? { ...history, [field]: value } : history
      )),
    }));
  };

  const handleCancel = () => {
    setForm(profile ? mapProfileToForm(profile) : emptyForm());
    setEditing(false);
    setNotice(null);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      setNotice({ type: 'error', message: 'Nama lengkap dan nomor WhatsApp wajib diisi.' });
      return;
    }

    setSaving(true);
    setNotice(null);

    try {
      const response = await applicationService.updateCandidateProfile({
        ...form,
        age: form.age ? Number(form.age) : null,
        height: form.height ? Number(form.height) : null,
        weight: form.weight ? Number(form.weight) : null,
      });
      const updatedProfile = response.data?.data;
      setProfile(updatedProfile);
      setForm(mapProfileToForm(updatedProfile));
      setEditing(false);
      setNotice({ type: 'success', message: 'Profil kandidat berhasil diperbarui.' });
    } catch (error) {
      setNotice({
        type: 'error',
        message: getUserFacingError(error, 'Profil kandidat belum dapat disimpan.').message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="prf-card prf-candidate">
        <div className="prf-candidate__loading"><span className="prf-spinner" /> Memuat profil kandidat...</div>
      </section>
    );
  }

  if (!hasProfile) {
    return (
      <section className="prf-card prf-candidate">
        <div className="prf-section-head">
          <h3 className="prf-section-title"><FiBriefcase size={16} /> Profil Kandidat</h3>
        </div>
        <div className="prf-candidate__empty">
          Profil kandidat akan dibuat saat kamu mengirim lamaran pertama.
        </div>
      </section>
    );
  }

  return (
    <section className="prf-card prf-candidate">
      <div className="prf-section-head">
        <div>
          <h3 className="prf-section-title"><FiBriefcase size={16} /> Profil Kandidat</h3>
          <p className="prf-candidate__subtitle">Data ini otomatis digunakan untuk lamaran berikutnya.</p>
        </div>
        {!editing && (
          <button type="button" className="prf-btn prf-btn--outline" onClick={() => setEditing(true)}>
            <FiEdit2 size={14} /> Edit Profil Kandidat
          </button>
        )}
      </div>

      {notice && <div className={`prf-candidate__notice prf-candidate__notice--${notice.type}`}>{notice.message}</div>}

      {!editing ? (
        <>
          <div className="prf-candidate__summary">
            {profileSummary.map((item) => (
              <div key={item.label} className="prf-candidate__summary-item">
                <span>{item.label}</span>
                <strong>{item.value || '-'}</strong>
              </div>
            ))}
          </div>
          <div className="prf-candidate__description">
            <span>Deskripsi diri</span>
            <p>{profile?.description || 'Belum diisi.'}</p>
          </div>
        </>
      ) : (
        <div className="prf-candidate__form">
          <div className="prf-candidate__grid">
            <label>
              <span>Nama lengkap *</span>
              <input value={form.name} onChange={(event) => updateField('name', event.target.value)} />
            </label>
            <label>
              <span>Email</span>
              <input value={form.email} readOnly aria-readonly="true" />
            </label>
            <label>
              <span>No. WhatsApp *</span>
              <input type="tel" value={form.phone} onChange={(event) => updateField('phone', event.target.value)} />
            </label>
            <label>
              <span>Domisili</span>
              <input value={form.domicile} onChange={(event) => updateField('domicile', event.target.value)} />
            </label>
            <label>
              <span>Jenis kelamin</span>
              <select value={form.gender} onChange={(event) => updateField('gender', event.target.value)}>
                <option value="">Pilih</option>
                <option value="male">Laki-laki</option>
                <option value="female">Perempuan</option>
              </select>
            </label>
            <label>
              <span>Tanggal lahir</span>
              <input type="date" value={form.birth_date} onChange={(event) => updateField('birth_date', event.target.value)} />
            </label>
            <label>
              <span>Umur</span>
              <input type="number" min="16" value={form.age} onChange={(event) => updateField('age', event.target.value)} />
            </label>
            <label>
              <span>Posisi yang dicari</span>
              <input value={form.position} onChange={(event) => updateField('position', event.target.value)} />
            </label>
            <label>
              <span>Tinggi badan (cm)</span>
              <input type="number" value={form.height} onChange={(event) => updateField('height', event.target.value)} />
            </label>
            <label>
              <span>Berat badan (kg)</span>
              <input type="number" value={form.weight} onChange={(event) => updateField('weight', event.target.value)} />
            </label>
            <label>
              <span>Media sosial</span>
              <input value={form.socmed_url} onChange={(event) => updateField('socmed_url', event.target.value)} />
            </label>
            <label>
              <span>Sumber info lowongan</span>
              <input value={form.resources} onChange={(event) => updateField('resources', event.target.value)} />
            </label>
          </div>

          <label className="prf-candidate__wide-field">
            <span>Deskripsi diri</span>
            <textarea rows={5} value={form.description} onChange={(event) => updateField('description', event.target.value)} />
          </label>

          <div className="prf-candidate__subsection">
            <h4>Pendidikan terakhir</h4>
            <div className="prf-candidate__grid">
              <label><span>Jenjang</span><input value={form.level} onChange={(event) => updateField('level', event.target.value)} /></label>
              <label><span>Jurusan</span><input value={form.major} onChange={(event) => updateField('major', event.target.value)} /></label>
              <label><span>Instansi / sekolah</span><input value={form.institute} onChange={(event) => updateField('institute', event.target.value)} /></label>
              <label><span>Lokasi instansi</span><input value={form.location} onChange={(event) => updateField('location', event.target.value)} /></label>
              <label><span>Tahun lulus</span><input value={form.year_end} onChange={(event) => updateField('year_end', event.target.value)} /></label>
            </div>
          </div>

          <div className="prf-candidate__subsection">
            <div className="prf-candidate__subsection-head">
              <h4>Riwayat pekerjaan</h4>
              <button
                type="button"
                className="prf-btn prf-btn--outline"
                onClick={() => setForm((current) => ({ ...current, jobs: [...current.jobs, emptyHistory()] }))}
              >
                <FiPlus /> Tambah
              </button>
            </div>
            {form.jobs.length === 0 && <p className="prf-candidate__muted">Belum ada riwayat pekerjaan.</p>}
            {form.jobs.map((history, index) => (
              <div key={index} className="prf-candidate__history">
                <div className="prf-candidate__grid">
                  <label><span>Posisi</span><input value={history.position} onChange={(event) => updateHistory(index, 'position', event.target.value)} /></label>
                  <label><span>Perusahaan / brand</span><input value={history.company_name} onChange={(event) => updateHistory(index, 'company_name', event.target.value)} /></label>
                  <label><span>Mulai</span><input type="date" value={history.join_date} onChange={(event) => updateHistory(index, 'join_date', event.target.value)} /></label>
                  <label><span>Selesai</span><input type="date" value={history.end_date} onChange={(event) => updateHistory(index, 'end_date', event.target.value)} /></label>
                </div>
                <label className="prf-candidate__wide-field">
                  <span>Deskripsi pekerjaan</span>
                  <textarea rows={3} value={history.jobdesc} onChange={(event) => updateHistory(index, 'jobdesc', event.target.value)} />
                </label>
                <button
                  type="button"
                  className="prf-candidate__remove"
                  onClick={() => setForm((current) => ({
                    ...current,
                    jobs: current.jobs.filter((_, historyIndex) => historyIndex !== index),
                  }))}
                >
                  <FiTrash2 /> Hapus riwayat
                </button>
              </div>
            ))}
          </div>

          <div className="prf-candidate__documents">
            <div>
              <h4>Foto kandidat</h4>
              <ImageUploader value={form.photogrid} onChange={(value) => updateField('photogrid', value)} />
            </div>
            <div>
              <h4>CV</h4>
              <FileUploader
                value={form.cv}
                accept=".pdf,.doc,.docx,.txt"
                onChange={(value) => updateField('cv', value)}
                desc="PDF, DOC, DOCX, atau TXT"
              />
            </div>
          </div>

          <div className="prf-edit-actions prf-candidate__actions">
            <button type="button" className="prf-btn prf-btn--ghost" onClick={handleCancel} disabled={saving}>
              <FiX /> Batal
            </button>
            <button type="button" className="prf-btn prf-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? <span className="prf-spinner" /> : <FiCheck />}
              {saving ? 'Menyimpan...' : 'Simpan Profil'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CandidateProfileEditor;
